import { type B2bOrderItem, describeOrderFile, PAYMENT_REJECTION_REASONS, type PaymentRecord } from '@api/b2b';
import { confirmPortalPi, getPortalFileUrl, getPortalOrder, getPortalPackingListUrl, receivePortalDispatch, submitPortalPayment } from '@api/b2bPortal';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, FileInput, NumberInput, Select, TextInput } from '@mantine/core';
import { ArrowLeftIcon, FileXlsIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import styles from '../b2b.module.css';
import { Card, Field, OrderSteps, Pill, QuoteTotals } from '../shared';
import { errorMessage, formatDay, money, openSignedFile, splitSavedLines, todayIst, units } from '../utils';


const CUSTOMER_STEP_LABELS = {
    Submitted: 'Placed',
    'PI Issued': 'PI received',
    'PI Confirmed': 'PI confirmed',
    'Payment Submitted': 'Payment sent',
    'Payment Done': 'Payment verified',
    Shipped: 'Dispatched',
    Fulfilled: 'Received',
} as const;

const NEXT: Record<string, string> = {
    Submitted: 'The brand is reviewing your order. You will get a proforma invoice (PI) by email.',
    'PI Issued': 'Check the PI and confirm it.',
    'PI Confirmed': 'Pay the amount due and upload the payment proof.',
    'Payment Submitted': 'The brand is verifying your payment.',
    'Payment Done': 'Payment verified. The brand is packing your order.',
    Shipped: 'Your order is on its way. Mark each dispatch received when it arrives.',
    Fulfilled: 'Delivered. Thank you.',
    Cancelled: 'This order was cancelled.',
};

const PAYMENT_METHODS = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'CASH', 'OTHER'];

const PortalOrderDetail: React.FC = () => {
    const { id = '' } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data, error } = useQuery({ queryKey: ['portal-order', id], queryFn: () => getPortalOrder(id) });
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [payment, setPayment] = useState({ amount: 0, paidOn: todayIst(), method: 'NEFT', reference: '', note: '' });
    const [proof, setProof] = useState<File | null>(null);
    const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

    const refresh = () => {
        void queryClient.invalidateQueries({ queryKey: ['portal-order', id] });
        void queryClient.invalidateQueries({ queryKey: ['portal-orders'] });
        void queryClient.invalidateQueries({ queryKey: ['portal-home'] });
    };
    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setActionError(null);
        try {
            await action();
            refresh();
            return true;
        } catch (e) {
            setActionError(errorMessage(e));
            return false;
        } finally {
            setBusy(false);
        }
    };

    if (error) return <div className="page-shell"><span className={styles.error}>{errorMessage(error)}</span></div>;
    if (!data) return <div className="page-shell"><span className={styles.muted}>Loading…</span></div>;

    const { order, payments, dispatches, files } = data;
    const pis = files.filter((k) => /\/pi-rev\d+\.pdf$/.test(k)).sort((a, b) => Number(/rev(\d+)/.exec(b)?.[1]) - Number(/rev(\d+)/.exec(a)?.[1]));
    const latestPi = pis[0];
    const due = Math.max(0, payments.dueBeforeDispatch - payments.settled);
    const canPay = order.status === 'PI Confirmed' || order.status === 'Payment Submitted';
    const lineColumns: DataSheetColumn<B2bOrderItem>[] = [
        {
            key: 'description',
            header: 'Product',
            width: 300,
            render: (v: string | null, r) => (
                <div className={styles.row}>
                    <span>{v ?? r.itemId}</span>
                    {r.lineItemId.startsWith('FREE-') ? <Pill label="Free" tone="positive" /> : null}
                    {r.status === 'Cancelled' ? <Pill label="Not supplied" tone="muted" /> : null}
                </div>
            ),
        },
        { key: 'quantity', header: 'Units', width: 90, align: 'right', render: (v: number) => units(v) },
        { key: 'unitPrice', header: 'Price', width: 110, align: 'right', render: (v: number | null) => money(v) },
        { key: 'gstRate', header: 'GST', width: 70, align: 'right', render: (v: number | null) => (v === null ? '—' : `${v}%`) },
        { key: 'lineTotal', header: 'Value', width: 120, align: 'right', render: (v: number | null) => money(v) },
    ];
    const paymentColumns: DataSheetColumn<PaymentRecord>[] = [
        { key: 'paidOn', header: 'Paid on', width: 110, render: (v: string) => formatDay(v) },
        { key: 'amount', header: 'Amount', width: 120, align: 'right', render: (v: number) => money(v) },
        { key: 'reference', header: 'Reference', width: 180 },
        {
            key: 'status',
            header: 'Status',
            width: 260,
            render: (v: string | undefined, r) => (
                <div className={styles.row}>
                    <Pill label={v === 'SUBMITTED' ? 'Verifying' : v === 'REJECTED' ? 'Rejected' : 'Verified'} tone={v === 'REJECTED' ? 'critical' : v === 'SUBMITTED' ? 'warning' : 'positive'} />
                    {v === 'REJECTED' ? (
                        <span className={styles.muted}>
                            {PAYMENT_REJECTION_REASONS.find((x) => x.value === r.rejectionReason)?.label}
                            {r.rejectionNote ? `: ${r.rejectionNote}` : ''}
                        </span>
                    ) : null}
                </div>
            ),
        },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <div className={styles.between}>
                <PageHeader icon="📦" title={order.purchaseOrderId} subtitle={[formatDay(order.date), order.poRefNum ? `your PO ${order.poRefNum}` : null, order.shipToName].filter(Boolean).join(' · ')} />
                <Button variant={ButtonVariant.DEFAULT} leftSection={<ArrowLeftIcon size={16} />} onClick={() => navigate('/portal/orders')}>
                    My orders
                </Button>
            </div>
            <Card title="Progress" hint={NEXT[order.status]}>
                <OrderSteps status={order.status} labels={CUSTOMER_STEP_LABELS} />
            </Card>
            <div className={styles.layout}>
                <div className={styles.main}>
                    {order.status === 'PI Issued' ? (
                        <Card title="Proforma invoice" hint={`Valid until ${formatDay(order.expiryDate)}. Confirming tells the brand to proceed; payment follows.`}>
                            <div className={styles.row}>
                                {latestPi ? (
                                    <Button size="xs" variant={ButtonVariant.OUTLINED} onClick={() => void openSignedFile(() => getPortalFileUrl(id, latestPi))}>
                                        Download PI
                                    </Button>
                                ) : null}
                                <Button size="xs" variant={ButtonVariant.ACTION} loading={busy} onClick={() => void run(() => confirmPortalPi(id))}>
                                    Confirm PI
                                </Button>
                            </div>
                        </Card>
                    ) : null}

                    {canPay ? (
                        <Card title="Pay" hint={`Due before dispatch: ${money(due)}. Upload the bank receipt or UPI screenshot after paying.`}>
                            <div className={styles.fieldGrid}>
                                <NumberInput label="Amount paid" required min={0} decimalScale={2} value={payment.amount || ''} onChange={(v) => setPayment((p) => ({ ...p, amount: Number(v) || 0 }))} />
                                <TextInput label="Paid on" required type="date" value={payment.paidOn} onChange={(e) => { const value = e.currentTarget.value; setPayment((p) => ({ ...p, paidOn: value })); }} />
                                <Select label="Method" required allowDeselect={false} data={PAYMENT_METHODS} value={payment.method} onChange={(v) => setPayment((p) => ({ ...p, method: v ?? 'NEFT' }))} />
                                <TextInput label="UTR / reference" required value={payment.reference} onChange={(e) => { const value = e.currentTarget.value; setPayment((p) => ({ ...p, reference: value })); }} />
                            </div>
                            <FileInput label="Payment proof" required placeholder="PDF, JPG or PNG, up to 10 MB" accept="application/pdf,image/jpeg,image/png,image/webp" value={proof} onChange={setProof} />
                            <TextInput label="Note (optional)" value={payment.note} onChange={(e) => { const value = e.currentTarget.value; setPayment((p) => ({ ...p, note: value })); }} />
                            {duplicateWarning ? <span className={styles.error}>{duplicateWarning}</span> : null}
                            <Button
                                variant={ButtonVariant.ACTION}
                                w="fit-content"
                                loading={busy}
                                disabled={!proof || payment.amount <= 0 || !payment.paidOn || !payment.method || !payment.reference.trim()}
                                onClick={() =>
                                    void run(async () => {
                                        const r = await submitPortalPayment(id, { ...payment, note: payment.note.trim() || undefined }, proof as File);
                                        setDuplicateWarning(r.duplicateOf.length ? `This reference is also on ${r.duplicateOf.join(', ')}. The brand will check it.` : null);
                                        setProof(null);
                                        setPayment((p) => ({ ...p, amount: 0, reference: '', note: '' }));
                                    })
                                }
                            >
                                Submit payment
                            </Button>
                        </Card>
                    ) : null}

                    {actionError ? <span className={styles.error}>{actionError}</span> : null}

                    <div className={styles.sheet}>
                        <DataSheet<B2bOrderItem> title="Lines" data={order.items} columns={lineColumns} getRowKey={(r) => `${r.itemId}:${r.lineItemId}`} maxHeight="auto" />
                    </div>

                    {payments.payments.length ? (
                        <div className={styles.sheet}>
                            <DataSheet<PaymentRecord> title="Payments" data={payments.payments} columns={paymentColumns} getRowKey={(r) => r.id} maxHeight="auto" />
                        </div>
                    ) : null}

                    {dispatches.dispatches
                        .filter((d) => d.status !== 'DRAFT' && d.status !== 'APPROVED' && d.status !== 'CANCELLED')
                        .map((d) => (
                            <Card
                                key={d.id}
                                title={`Dispatch ${d.code}`}
                                actions={
                                    <div className={styles.row}>
                                        <Button size="xs" variant={d.status === 'RECEIVED' ? ButtonVariant.ACTION : ButtonVariant.DEFAULT} leftSection={<FileXlsIcon size={14} />} onClick={() => void run(() => openSignedFile(() => getPortalPackingListUrl(id, d.id)))}>
                                            Download packing list
                                        </Button>
                                        {d.status === 'RECEIVED' ? (
                                            <Pill label={`Received ${formatDay(d.receivedAt)}`} tone="positive" />
                                        ) : (
                                            <Button size="xs" variant={ButtonVariant.ACTION} loading={busy} onClick={() => void run(() => receivePortalDispatch(id, d.id))}>
                                                Mark received
                                            </Button>
                                        )}
                                    </div>
                                }
                            >
                                {d.dispatchDetails ? (
                                    <div className={styles.fieldGrid}>
                                        <Field label="Transporter" value={d.dispatchDetails.transporterName} />
                                        <Field label="LR / AWB" value={d.dispatchDetails.trackingNumber} />
                                        <Field label="Dispatched" value={formatDay(d.dispatchDetails.dispatchDate)} />
                                        <Field label="Invoice" value={`${d.dispatchDetails.invoiceNumber} · ${formatDay(d.dispatchDetails.invoiceDate)}`} />
                                    </div>
                                ) : null}
                                <span className={styles.cardHint}>{d.items.map((i) => `${order.items.find((x) => x.itemId === i.itemId)?.description ?? i.itemId} × ${units(i.plannedQty)}`).join(' · ')}</span>
                                {d.dispatchDetails?.documents.length ? (
                                    <div className={styles.row}>
                                        {d.dispatchDetails.documents.map((doc) => (
                                            <Button key={doc.fileKey} size="xs" variant={ButtonVariant.SUBTLE} onClick={() => void openSignedFile(() => getPortalFileUrl(id, doc.fileKey))}>
                                                {doc.fileName}
                                            </Button>
                                        ))}
                                    </div>
                                ) : null}
                            </Card>
                        ))}
                </div>
                <div className={styles.rail}>
                    <Card title="Amount">
                        <QuoteTotals
                            quote={{
                                charges: order.additionalCharges ?? [],
                                totals: { subtotal: order.subtotal, taxAmount: order.taxAmount, total: order.total, payable: payments.payable },
                                ...splitSavedLines(order.items),
                            }}
                        />
                        <div className={`${styles.fieldGrid} ${styles.divider}`}>
                            <Field label="Paid and verified" value={money(payments.settled)} />
                            <Field label="Outstanding" value={money(payments.outstanding)} />
                        </div>
                    </Card>
                    {files.length ? (
                        <Card title="Documents">
                            {files.map((key) => (
                                <div key={key} className={styles.between}>
                                    <span className={styles.cardHint}>{describeOrderFile(key)}</span>
                                    <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => void openSignedFile(() => getPortalFileUrl(id, key))}>
                                        Open
                                    </Button>
                                </div>
                            ))}
                        </Card>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default PortalOrderDetail;
